import * as THREE from 'three';
import { AudioHandle, PositionalOptions } from './AudioManager.types';

export { AudioHandle, PositionalOptions };

export class AudioManager {
    private readonly loader = new THREE.AudioLoader();
    private readonly bufferCache = new Map<string, AudioBuffer>();
    private readonly activeHandles = new Map<number, AudioHandle>();
    private readonly pendingPlays: Array<() => void> = [];
    private readonly preloadQueue = new Set<string>();
    private masterVolume = 1;
    private muted = false;
    private nextId = 0;
    private autoplayUnlocked = false;

    constructor(private readonly listener: THREE.AudioListener) {
        const unlock = () => {
            if (this.autoplayUnlocked) { return; }
            this.autoplayUnlocked = true;
            this.flushPendingPlays();
            document.removeEventListener('pointerdown', unlock);
        };
        document.addEventListener('pointerdown', unlock);
    }

    public registerPreload(...srcs: string[]): void {
        srcs.forEach(src => this.preloadQueue.add(src));
    }

    public preload(loadFn: (src: string, onLoaded: (buffer: AudioBuffer) => void) => void): void {
        this.preloadQueue.forEach(src => loadFn(src, (buffer) => this.cacheBuffer(src, buffer)));
    }

    public cacheBuffer(src: string, buffer: AudioBuffer): void {
        this.bufferCache.set(src, buffer);
    }

    public playMusic(src: string, volume = 1, loop = false): AudioHandle {
        const audio = new THREE.Audio(this.listener);
        const handle = this.createHandle(audio, src);
        const play = () => this.startAudio(audio, src, volume, loop, handle.id);
        if (this.autoplayUnlocked) { play(); } else { this.pendingPlays.push(play); }
        return handle;
    }

    public stopMusic(handle: AudioHandle, fadeOutMs = 0): void {
        this.activeHandles.delete(handle.id);
        const audio = handle.audio as THREE.Audio;
        if (!audio.isPlaying) { return; }
        if (fadeOutMs <= 0) {
            audio.stop();
            return;
        }
        const steps = 20;
        const stepTime = fadeOutMs / steps;
        const initial = audio.getVolume();
        let step = 0;
        const intervalId = setInterval(() => {
            step++;
            audio.setVolume(initial * (1 - step / steps));
            if (step >= steps) { clearInterval(intervalId); audio.stop(); }
        }, stepTime);
    }

    public crossfade(from: AudioHandle, to: string, durationMs: number): AudioHandle {
        this.stopMusic(from, durationMs);
        const toAudio = new THREE.Audio(this.listener);
        const handle = this.createHandle(toAudio, to);
        const doFadeIn = () => {
            this.startAudio(toAudio, to, 0, true, handle.id);
            const steps = 20;
            const stepTime = durationMs / steps;
            const target = this.muted ? 0 : this.masterVolume;
            let step = 0;
            const intervalId = setInterval(() => {
                step++;
                toAudio.setVolume(target * (step / steps));
                if (step >= steps) { clearInterval(intervalId); }
            }, stepTime);
        };
        if (this.autoplayUnlocked) { doFadeIn(); } else { this.pendingPlays.push(doFadeIn); }
        return handle;
    }

    public playPositional(src: string, object3D: THREE.Object3D, options: PositionalOptions = {}): AudioHandle {
        const audio = new THREE.PositionalAudio(this.listener);
        audio.setRefDistance(options.refDistance ?? 50);
        audio.setRolloffFactor(options.rolloffFactor ?? 1);
        object3D.add(audio);
        const handle = this.createHandle(audio, src);
        const volume = options.volume ?? 1;
        const loop = options.loop ?? false;
        const playbackRate = options.playbackRate ?? 1;
        const play = () => this.startAudio(audio, src, volume, loop, handle.id, playbackRate);
        if (this.autoplayUnlocked) { play(); } else { this.pendingPlays.push(play); }
        return handle;
    }

    public stopPositional(handle: AudioHandle): void {
        this.activeHandles.delete(handle.id);
        const audio = handle.audio;
        if (audio.isPlaying) { audio.stop(); }
        audio.parent?.remove(audio);
    }

    public setMasterVolume(v: number): void {
        this.masterVolume = v;
        if (!this.muted) { this.activeHandles.forEach((h) => h.audio.setVolume(v)); }
    }

    public mute(): void {
        if (this.muted) { return; }
        this.muted = true;
        this.activeHandles.forEach((h) => h.audio.setVolume(0));
    }

    public unmute(): void {
        if (!this.muted) { return; }
        this.muted = false;
        this.activeHandles.forEach((h) => h.audio.setVolume(this.masterVolume));
    }

    private startAudio(
        audio: THREE.Audio | THREE.PositionalAudio,
        src: string,
        volume: number,
        loop: boolean,
        handleId: number,
        playbackRate = 1,
    ): void {
        const doPlay = (buffer: AudioBuffer) => {
            if (!this.activeHandles.has(handleId)) { return; }
            audio.setBuffer(buffer);
            audio.setVolume(this.muted ? 0 : volume);
            audio.setLoop(loop);
            audio.play();
            if (playbackRate !== 1) {
                (audio as any).source.playbackRate.value = playbackRate;
            }
        };
        if (this.bufferCache.has(src)) {
            doPlay(this.bufferCache.get(src)!);
        } else {
            this.loader.load(src, (buffer) => {
                this.bufferCache.set(src, buffer);
                doPlay(buffer);
            });
        }
    }

    private createHandle(audio: THREE.Audio | THREE.PositionalAudio, src: string): AudioHandle {
        const handle: AudioHandle = { id: this.nextId++, src, audio };
        this.activeHandles.set(handle.id, handle);
        return handle;
    }

    private flushPendingPlays(): void {
        this.pendingPlays.splice(0).forEach((play) => play());
    }
}
